"use client";

import { Check, Copy } from "lucide-react";
import Image from "next/image";
import { useState } from "react";
import type { ExplorePromptCard } from "../types";

/** Maximum number of tags to display per prompt card. */
const MAX_VISIBLE_TAGS = 3;

interface PromptCardProps {
  prompt: ExplorePromptCard;
}

export function PromptCard({ prompt }: PromptCardProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();

    try {
      await navigator.clipboard.writeText(prompt.promptText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard may be unavailable depending on browser permissions.
      setCopied(false);
    }
  };

  return (
    <div
      className="hover:-translate-y-0.5 flex flex-col overflow-hidden rounded-2xl bg-[var(--explore-bg-card)] shadow-[var(--explore-card-shadow)] transition-all hover:shadow-[var(--explore-card-shadow-hover)]"
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
    >
      {/* Preview image — fixed height for consistency */}
      <div className="relative h-[180px] w-full shrink-0 overflow-hidden">
        <Image
          alt={prompt.title}
          className="h-full w-full object-cover transition-transform duration-300 hover:scale-[1.03]"
          fill
          sizes="(max-width: 640px) 100vw, 50vw"
          src={prompt.coverImageUrl}
        />
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col gap-2.5 p-5">
        <h3
          className="line-clamp-2 font-bold text-[1.25rem] text-[var(--explore-text-primary)] leading-[1.3]"
          style={{ fontFamily: "var(--explore-card-title-stack)" }}
        >
          {prompt.title}
        </h3>

        {/* Prompt text with yellow left border */}
        <div className="border-[var(--explore-accent-yellow)] border-l-[3px] pl-3.5">
          <p className="line-clamp-3 text-[0.85rem] text-[var(--explore-text-secondary)] leading-[1.6]">
            {prompt.promptText}
          </p>
        </div>

        {/* Footer: tags + copy button */}
        <div className="mt-auto flex items-center gap-2 pt-1">
          <div className="flex min-w-0 flex-wrap gap-1.5">
            {prompt.tags.slice(0, MAX_VISIBLE_TAGS).map((tag) => (
              <span
                className="min-w-0 truncate rounded-sm bg-[var(--explore-tag-bg)] px-2.5 py-1 font-medium text-[0.75rem] text-[var(--explore-tag-text)]"
                key={tag.slug}
              >
                {tag.displayName}
              </span>
            ))}
          </div>
          <button
            className="hover:-translate-y-px ml-auto flex shrink-0 items-center gap-1.5 rounded-full border-none bg-[var(--explore-accent-yellow)] px-3.5 py-1.5 font-semibold text-[0.75rem] text-[var(--explore-on-accent-text)] shadow-[0_2px_8px_rgba(255,192,2,0.4)] transition-all hover:shadow-[0_4px_12px_rgba(255,192,2,0.5)]"
            onClick={handleCopy}
            type="button"
          >
            {copied ? (
              <>
                <Check className="h-3.5 w-3.5" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5" />
                Copy prompt
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
